import React, { useEffect } from 'react'
import { trackEvent } from 'utils/analytics'
import { getCurrentLocale, formatCurrency, buildTeamShareUrl } from 'utils'
import { usePromotionCopy } from 'hooks'
import PromotionLinkModal from './PromotionLinkModal'
import { DAY_OF_WEEK_LABELS, formatSchedulePattern } from 'utils/scheduleUtils'
import VideoOrImage from '../shared/VideoOrImage'

function formatSessionDateTime(isoStart, isoEnd) {
  const start = new Date(isoStart)
  const end = isoEnd ? new Date(isoEnd) : null
  const m = start.getMonth() + 1
  const d = start.getDate()
  const day = DAY_OF_WEEK_LABELS[start.getDay()]
  const sh = String(start.getHours()).padStart(2, '0')
  const sm = String(start.getMinutes()).padStart(2, '0')
  let str = `${m}/${d}（${day}）${sh}:${sm}`
  if (end) {
    const eh = String(end.getHours()).padStart(2, '0')
    const em = String(end.getMinutes()).padStart(2, '0')
    str += ` – ${eh}:${em}`
  }
  return str
}

function EnrollStatusButton({ groupClass, label, colors, isFull, onClick, className = '', t = {} }) {
  const status = groupClass.enrollment_status

  if (status === 'registered') {
    return (
      <button disabled className={`py-2.5 rounded-xl text-sm font-semibold text-white cursor-not-allowed opacity-70 ${className}`} style={{ backgroundColor: '#9CA3AF' }}>
        {t.already_registered || 'Registered'}
      </button>
    )
  }
  if (status === 'pending_payment') {
    return (
      <button onClick={onClick} className={`py-2.5 rounded-xl text-sm font-semibold text-white cursor-pointer ${className}`} style={{ backgroundColor: '#F59E0B' }}>
        {t.pending_payment || 'Pending Payment'}
      </button>
    )
  }
  if (status === 'confirmed') {
    return (
      <button disabled className={`py-2.5 rounded-xl text-sm font-semibold text-white cursor-not-allowed opacity-70 ${className}`} style={{ backgroundColor: '#10B981' }}>
        {t.already_confirmed || 'Enrolled'}
      </button>
    )
  }

  return (
    <button
      onClick={isFull ? undefined : onClick}
      disabled={isFull}
      className={`py-2.5 rounded-xl text-sm font-semibold text-white transition-all cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      style={{ backgroundColor: isFull ? '#9CA3AF' : (colors?.primary || '#3366FF') }}
    >
      {isFull ? (t.full || 'Full') : label}
    </button>
  )
}

export default function GroupClassDetailModal({ groupClass, colors, translations = {}, teamSlug, onEnroll, onEnrollTrial, onClose, currentUser, teamId }) {
  const t = translations
  const { handleCopyClick, modalState, closeModal: closePromoModal, isCopied, inviteCode } = usePromotionCopy({ currentUser, teamId })
  const shareUrl = buildTeamShareUrl(teamSlug, { enroll_class: groupClass.id }, inviteCode)

  const sessions = groupClass.sessions || []
  const trialClasses = (groupClass.trial_classes || []).filter(tc => tc.status !== 'cancelled')
  const isFull = (groupClass.remaining_capacity ?? 0) <= 0
  const hasStarted = groupClass.has_started

  // ESC + body scroll lock
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const handleEsc = (e) => { if (e.key === 'Escape') onClose?.() }
    document.addEventListener('keydown', handleEsc)
    return () => {
      document.body.style.overflow = prev
      document.removeEventListener('keydown', handleEsc)
    }
  }, [onClose])

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 lg:p-4" onClick={onClose}>
      <div
        className="bg-white w-full h-full lg:h-auto lg:max-h-[90vh] lg:max-w-lg lg:mx-4 lg:rounded-xl lg:shadow-xl overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header — mobile: back arrow, desktop: blue bar */}
        <div className="sticky top-0 z-10 shrink-0">
          <div className="lg:hidden px-4 py-3 bg-white border-b border-gray-100 flex items-center gap-3">
            <button type="button" onClick={onClose} className="flex items-center text-gray-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-sm font-semibold text-gray-900 truncate flex-1">
              {t.group_class_detail || 'Class Details'}
            </span>
            <button onClick={() => handleCopyClick(shareUrl)} className="text-gray-400 hover:text-blue-500 p-1">
              {isCopied ? (
                <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              )}
            </button>
          </div>
          <div className="hidden lg:flex items-center justify-between px-6 py-4 border-b" style={{ backgroundColor: '#E6F0FF' }}>
            <span className="font-semibold truncate" style={{ color: '#3366FF' }}>
              {t.group_class_detail || 'Class Details'}
            </span>
            <div className="flex items-center gap-2">
              <button onClick={() => handleCopyClick(shareUrl)} className="text-gray-400 hover:text-blue-500 p-1 rounded-lg hover:bg-blue-50 cursor-pointer">
                {isCopied ? (
                  <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                )}
              </button>
              <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          <VideoOrImage videoUrl={groupClass.video_url} imageUrl={groupClass.image_url} alt={groupClass.title} />

          <div className="p-6 space-y-5">
            {/* Title + teacher */}
            <div>
              <h3 className="text-xl font-bold" style={{ color: '#222' }}>{groupClass.title}</h3>
              {groupClass.teacher_name && (
                <p className="text-sm mt-1" style={{ color: '#717171' }}>{groupClass.teacher_name}</p>
              )}
            </div>

            {/* Meta badges */}
            <div className="flex items-center gap-3 flex-wrap">
              {groupClass.total_sessions > 0 && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">
                  {groupClass.total_sessions} {t.sessions_unit || 'sessions'}
                  {groupClass.total_hours > 0 && ` · ${groupClass.total_hours % 1 === 0 ? groupClass.total_hours : groupClass.total_hours.toFixed(1)} ${t.hours_unit || 'hours'}`}
                </span>
              )}
              {groupClass.remaining_capacity != null && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  {isFull
                    ? (t.full || 'Full')
                    : (t.spots_left || '%{count} spots left').replace('%{count}', groupClass.remaining_capacity)}
                </span>
              )}
            </div>

            {/* Schedule pattern */}
            {groupClass.schedule_rule && (
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: colors?.primary || '#3366FF' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {formatSchedulePattern(groupClass.schedule_rule, { prefix: 'Every ' })}
              </div>
            )}

            {/* Description */}
            {groupClass.description && (
              <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: '#555' }}>
                {groupClass.description}
              </p>
            )}

            {/* Session schedule list */}
            {sessions.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  {t.schedule_list || 'Schedule'}
                </h4>
                <div className="rounded-lg border border-gray-100 divide-y divide-gray-100 max-h-48 overflow-y-auto">
                  {sessions.map((s, i) => (
                    <div key={s.id || i} className="flex items-center gap-3 px-3 py-2 text-sm">
                      <span className="text-xs text-gray-400 w-5 text-right shrink-0">{i + 1}</span>
                      <span className="text-gray-700">{formatSessionDateTime(s.start_time, s.end_time)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Price */}
            <div className="flex items-baseline gap-2 pt-2 border-t border-gray-100">
              <span className="text-sm text-gray-500">{t.total_price || 'Total Price'}</span>
              <span className="text-2xl font-bold" style={{ color: colors?.primary || '#3366FF' }}>
                {formatCurrency(groupClass.price, groupClass.currency || 'TWD')}
              </span>
            </div>
          </div>

          {/* Enrollment actions */}
          <div className="px-6 pb-6 space-y-3">
            {/* Trial classes — hide expired ones if any active trial exists */}
            {(() => {
              const activeTrials = trialClasses.filter(tc => !tc.has_expired)
              const displayTrials = activeTrials.length > 0 ? activeTrials : trialClasses
              if (displayTrials.length === 0) return null
              return (
              <div className="space-y-2">
                {displayTrials.map(trial => {
                  const trialPrice = trial.trial_price > 0 ? trial.trial_price : trial.price
                  const trialIsFree = trialPrice === 0
                  const trialIsFull = (trial.remaining_capacity ?? 0) <= 0
                  const trialExpired = trial.has_expired
                  const nearestSession = trial.sessions?.[0]
                  const trialDateStr = nearestSession ? formatSessionDateTime(nearestSession.start_time, nearestSession.end_time) : null

                  return (
                    <div key={trial.id} className={`rounded-lg px-4 py-3 ${trialExpired ? 'bg-gray-50' : 'bg-green-50'}`}>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className={`text-sm font-medium ${trialExpired ? 'text-gray-500' : 'text-green-800'}`}>
                          {t.trial_lesson || 'Trial Lesson'}
                        </span>
                        <span className={`text-sm font-semibold ${trialExpired ? 'text-gray-400' : 'text-green-700'}`}>
                          {trialIsFree ? (t.free || 'Free') : formatCurrency(trialPrice, groupClass.currency || 'TWD')}
                        </span>
                      </div>
                      {trialDateStr && (
                        <p className={`text-xs mb-2 ${trialExpired ? 'text-gray-400' : 'text-green-600'}`}>{trialDateStr}</p>
                      )}
                      {trialExpired ? (
                        <button
                          disabled
                          className="w-full py-2.5 rounded-xl text-sm font-semibold text-white cursor-not-allowed opacity-70"
                          style={{ backgroundColor: '#9CA3AF' }}
                        >
                          {t.trial_expired || 'Ended'}
                        </button>
                      ) : (
                        <EnrollStatusButton
                          groupClass={trial}
                          label={t.enroll_trial || 'Enroll Trial'}
                          colors={{ primary: '#10B981' }}
                          isFull={trialIsFull}
                          onClick={() => onEnrollTrial?.(trial)}
                          t={t}
                          className="w-full"
                        />
                      )}
                    </div>
                  )
                })}
              </div>
              )
            })()}

            {/* Regular class enroll */}
            {hasStarted ? (
              <button
                disabled
                className="w-full py-2.5 rounded-xl text-sm font-semibold text-white cursor-not-allowed opacity-70"
                style={{ backgroundColor: '#9CA3AF' }}
              >
                {t.already_started || 'Already Started'}
              </button>
            ) : (
              <EnrollStatusButton
                groupClass={groupClass}
                label={groupClass.enrollment_mode === 'register_first'
                  ? (t.register_first || 'Register')
                  : (t.enroll_button || 'Enroll')}
                colors={colors}
                isFull={isFull}
                onClick={() => onEnroll?.(groupClass)}
                className="w-full"
                t={t}
              />
            )}
          </div>
        </div>
      </div>
      <PromotionLinkModal
        isOpen={modalState.isOpen}
        onClose={closePromoModal}
        baseUrl={modalState.baseUrl}
        teamId={teamId}
        inviteCode={inviteCode}
        colors={colors}
        translations={t?.promotionModal}
      />
    </div>
  )
}
